import React, { useState } from 'react';
import { Box, Grid, Divider, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DonutLargeIcon from '@material-ui/icons/DonutLarge';
import DeleteIcon from '@material-ui/icons/Delete';
// import FlagIcon from '@material-ui/icons/Flag';
// import PersonIcon from '@material-ui/icons/Person';
import { MDTypography } from '../../../components/MDTypography';
import ConsentPopup from '../../../components/ConsentPopup/ConsentPopup';
import {
  TaskDetail,
  TaskStatus,
  UpdateTaskInput,
} from '../../../types/interfaces/task.types';
// TaskPriority,
// TaskActor,

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    borderRight: `1px solid ${theme.palette.divider}`,
    height: '100%',
    overflowY: 'auto',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  detailItem: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    cursor: 'pointer',
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
    },
  },
  detailLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
}));

interface TaskSidePanelProps {
  task: TaskDetail;
  updateTask: (input: UpdateTaskInput) => Promise<boolean>;
  onTaskUpdated: () => void;
  onTaskDeleted: (task: TaskDetail) => void;
}

const formatEnumText = (text: string) => {
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const TaskSidePanel: React.FC<TaskSidePanelProps> = ({
  task,
  updateTask,
  onTaskUpdated,
  onTaskDeleted,
}) => {
  const classes = useStyles();

  // Menu states
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [priorityAnchorEl, setPriorityAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [assignAnchorEl, setAssignAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handlePriorityClick = (event: React.MouseEvent<HTMLElement>) => {
    setPriorityAnchorEl(event.currentTarget);
  };

  const handleAssignClick = (event: React.MouseEvent<HTMLElement>) => {
    setAssignAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setStatusAnchorEl(null);
    setPriorityAnchorEl(null);
    setAssignAnchorEl(null);
  };

  const handleStatusSelect = async (status: TaskStatus) => {
    handleClose();
    if (task.status !== status) {
      const success = await updateTask({ status });
      if (success) onTaskUpdated();
    }
  };

  const handleDeleteTaskClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    await onTaskDeleted(task);
    setOpenDeleteDialog(false);
  };

  // const handlePrioritySelect = async (priority: TaskPriority) => {
  //   if (task.priority !== priority) {
  //     const success = await updateTask({ priority });
  //     if (success) onTaskUpdated();
  //   }
  // };

  // const handleAssignSelect = async (actor: TaskActor) => {
  //   handleClose();
  //   if (task.responsibleActor !== actor) {
  //     const success = await updateTask({ responsibleActor: actor });
  //     if (success) onTaskUpdated();
  //   }
  // };

  return (
    <Box className={classes.root}>
      {/* Details Row: Status | Priority | Assign */}
      <Box className={classes.detailsRow}>
        {/* Status */}
        <Box className={classes.detailItem} onClick={handleStatusClick}>
          <Box className={classes.detailLabel}>
            <DonutLargeIcon fontSize="small" />
            <MDTypography variant="caption">Status</MDTypography>
          </Box>
          <MDTypography className={classes.detailValue} variant="caption">
            {formatEnumText(task.status)}
          </MDTypography>
        </Box>
        <Menu
          anchorEl={statusAnchorEl}
          keepMounted
          open={Boolean(statusAnchorEl)}
          onClose={handleClose}
        >
          {Object.values(TaskStatus).map((status) => (
            <MenuItem key={status} onClick={() => handleStatusSelect(status)}>
              {formatEnumText(status)}
            </MenuItem>
          ))}
        </Menu>
        <Box className={classes.detailItem} onClick={handleDeleteTaskClick}>
          <Box className={classes.detailLabel}>
            <DeleteIcon fontSize="small" />
            <MDTypography variant="caption">Delete</MDTypography>
          </Box>
          <MDTypography className={classes.detailValue} variant="caption">
            Delete
          </MDTypography>
        </Box>

        {/* Priority */}
        {/* <Box className={classes.detailItem} onClick={handlePriorityClick}>
          <Box className={classes.detailLabel}>
            <FlagIcon fontSize="small" />
            <MDTypography variant="caption">Priority</MDTypography>
          </Box>
          <MDTypography className={classes.detailValue} variant="caption">
            {formatEnumText(task.priority)}
          </MDTypography>
        </Box>
        <Menu
          anchorEl={priorityAnchorEl}
          keepMounted
          open={Boolean(priorityAnchorEl)}
          onClose={handleClose}
        >
          {Object.values(TaskPriority).map((priority) => (
            <MenuItem
              key={priority}
              onClick={() => handlePrioritySelect(priority)}
            >
              {formatEnumText(priority)}
            </MenuItem>
          ))}
        </Menu> */}

        {/* Assign */}
        {/* <Box className={classes.detailItem} onClick={handleAssignClick}>
          <Box className={classes.detailLabel}>
            <PersonIcon fontSize="small" />
            <MDTypography variant="caption">Assign</MDTypography>
          </Box>
          <MDTypography className={classes.detailValue} variant="caption">
            {formatEnumText(task.responsibleActor)}
          </MDTypography>
        </Box>
        <Menu
          anchorEl={assignAnchorEl}
          keepMounted
          open={Boolean(assignAnchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={() => handleAssignSelect(TaskActor.FAIRFARE)}>
            FairFare
          </MenuItem>
          <MenuItem onClick={() => handleAssignSelect(TaskActor.CARRIER)}>
            Carrier
          </MenuItem>
        </Menu> */}
      </Box>

      {/* Instructions */}
      <Box className={classes.section}>
        <MDTypography className={classes.sectionTitle}>
          Instructions
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {task.topic?.description || 'No instruction provided.'}
        </MDTypography>
      </Box>

      <Divider className={classes.section} />

      {/* Description */}
      <Box className={classes.section}>
        <MDTypography className={classes.sectionTitle}>
          Description
        </MDTypography>
        <div>
          <MDTypography variant="caption" color="text">
            <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
              Category:
            </span>{' '}
            {task.topic?.name}
          </MDTypography>
        </div>
        <div>
          <MDTypography variant="caption" color="text">
            <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
              Category Description:
            </span>{' '}
            {task.topic?.description}
          </MDTypography>
        </div>
        <div>
          <MDTypography variant="caption" color="text">
            <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
              User additional details:
            </span>{' '}
            {task.description || 'N/A'}
          </MDTypography>
        </div>
      </Box>

      <Divider className={classes.section} />

      {/* Linked Items */}
      <Box className={classes.section}>
        <MDTypography className={classes.sectionTitle}>
          Linked Items
        </MDTypography>
        <Grid container spacing={2}>
          {task.billId && (
            <Grid item xs={6}>
              <MDTypography
                variant="caption"
                style={{ color: 'rgba(0, 0, 0, 0.54)' }}
              >
                Bill ID
              </MDTypography>
              <MDTypography variant="body2">{task.billId}</MDTypography>
            </Grid>
          )}
          {task.claimId && (
            <Grid item xs={6}>
              <MDTypography
                variant="caption"
                style={{ color: 'rgba(0, 0, 0, 0.54)' }}
              >
                Claim ID
              </MDTypography>
              <MDTypography variant="body2">{task.claimId}</MDTypography>
            </Grid>
          )}
          {task.caseId && (
            <Grid item xs={6}>
              <MDTypography
                variant="caption"
                style={{ color: 'rgba(0, 0, 0, 0.54)' }}
              >
                Case ID
              </MDTypography>
              <MDTypography variant="body2">{task.caseId}</MDTypography>
            </Grid>
          )}
          {task.policyNumber && (
            <Grid item xs={6}>
              <MDTypography
                variant="caption"
                style={{ color: 'rgba(0, 0, 0, 0.54)' }}
              >
                Policy Number
              </MDTypography>
              <MDTypography variant="body2">{task.policyNumber}</MDTypography>
            </Grid>
          )}
          {!task.billId &&
            !task.claimId &&
            !task.caseId &&
            !task.policyNumber && (
              <Grid item xs={12}>
                <MDTypography
                  variant="body2"
                  style={{ color: 'rgba(0, 0, 0, 0.54)' }}
                >
                  No linked items.
                </MDTypography>
              </Grid>
            )}
        </Grid>
      </Box>
      <ConsentPopup
        openDialog={openDeleteDialog}
        handleConfirm={handleConfirmDelete}
        handleCancel={() => setOpenDeleteDialog(false)}
        title="Delete Task"
        text="Are you sure you want to delete this task?"
      />
    </Box>
  );
};

export default TaskSidePanel;
